/**
 * The unit palette (WORKBOARD §10, decision 21 — adopted for E2 so the owner can judge it on
 * the models). The world keeps its pastel matte palette; a unit is **one hue in three values**:
 * a saturated body (the whole person is the team, Ravenfield), a dark (helmet, boots, straps)
 * and a light flash (visor, armband), so that on any ground at least one part separates by
 * value. Measured on every archetype's ground layers in design_sheets/style.html: the body
 * reads on 97 of 100 team × archetype cells (today's pastel: 59) and blends on none (today: 3).
 *
 * Rendering only: derived from `TeamPalette.primary`, no game state.
 */
import type { TeamPalette } from '../types';

export interface UnitPalette {
  body: string;    // the team, saturated, mid value
  dark: string;    // same hue, dark: helmet, boots, belt, flap
  flash: string;   // same hue, light: visor, armband, a stripe
  metal: string;   // weapons and mechanisms, team-neutral
}

const METAL = '#2b2e33';

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16) / 255) as [number, number, number];
}

function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map(v => Math.round(Math.max(0, Math.min(1, v)) * 255).toString(16).padStart(2, '0')).join('');
}

function rgbToHueSat([r, g, b]: [number, number, number]): [number, number] {
  const max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2;
  if (max === min) return [0, 0];
  const d = max - min, s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [h / 6, s];
}

export function hslHex(h: number, s: number, l: number): string {
  if (s === 0) return rgbToHex(l, l, l);
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s, p = 2 * l - q;
  const f = (t: number) => {
    t = ((t % 1) + 1) % 1;
    return t < 1 / 6 ? p + (q - p) * 6 * t : t < 1 / 2 ? q : t < 2 / 3 ? p + (q - p) * (2 / 3 - t) * 6 : p;
  };
  return rgbToHex(f(h + 1 / 3), f(h), f(h - 1 / 3));
}

/** The three-value unit palette for a team: its hue, saturated, at fixed lightnesses. */
export function unitPaletteFor(team: Pick<TeamPalette, 'primary'>): UnitPalette {
  const [h, s] = rgbToHueSat(hexToRgb(team.primary));
  const sat = Math.min(0.78, s * 1.6 + 0.18);
  return {
    body: hslHex(h, sat, 0.46),
    dark: hslHex(h, Math.min(0.6, sat), 0.2),
    flash: hslHex(h, 0.9, 0.8),
    metal: METAL,
  };
}

/** Today's pastel team colours read as the same three values — for side-by-side comparison. */
export function classicUnitPalette(team: Pick<TeamPalette, 'primary' | 'accent' | 'visor'>): UnitPalette {
  return { body: team.primary, dark: team.accent, flash: team.visor, metal: '#3a3c40' };
}
